import React, { useEffect, useState, useSyncExternalStore } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Icon,
  Stack,
  Typography,
} from '@mui/material';

import { termsAcknowledgement } from '../../services/utils/termsAcknowledgement';
import { helperService } from '../../services/helper.service';
import ErrorMessage from '../controls/ErrorMessage';

// Blocking dialog for mandatory acceptance of updated terms. It cannot be
// dismissed (no close button, no backdrop close, no escape) — the user can only
// continue by acknowledging the terms.
const TermsAcknowledgementDialog: React.FC = () => {
  const state = useSyncExternalStore(termsAcknowledgement.subscribe, termsAcknowledgement.getState);
  const [agreed, setAgreed] = useState(false);

  const { isOpen, loading, acknowledging, pendingTerms, loadError, acknowledgeError } = state;

  useEffect(() => {
    if (isOpen) {
      setAgreed(false);
    }
  }, [isOpen, pendingTerms]);

  const handleAcknowledge = () => {
    void termsAcknowledgement.acknowledge();
  };

  const handleRetryLoad = () => {
    void termsAcknowledgement.loadPendingTerms();
  };

  return (
    <Dialog
      className="dialog"
      open={isOpen}
      onClose={() => {}}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      aria-labelledby="terms-ack-title"
      sx={{ zIndex: 100000 }}
    >
      <DialogTitle id="terms-ack-title">
        {pendingTerms?.title ?? 'Voorwaarden'}
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Stack alignItems="center" sx={{ py: 4 }} spacing={2}>
            <CircularProgress />
            <Typography>De voorwaarden worden geladen…</Typography>
          </Stack>
        )}

        {!loading && loadError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRetryLoad}>
                Opnieuw proberen
              </Button>
            }
          >
            Het laden van de voorwaarden is mislukt. Probeer het opnieuw.
          </Alert>
        )}

        {!loading && !loadError && pendingTerms && (
          <>
            {/* Terms body is trusted admin-authored HTML from the backend, rendered the
                same way as the application's other rich-text dialogs (DialogBase). */}
            <DialogContentText
              component="div"
              sx={{ whiteSpace: 'pre-line' }}
              dangerouslySetInnerHTML={{ __html: pendingTerms.body }}
            />

            {pendingTerms.activatedAt && (
              <span className="note">
                Gepubliceerd sinds {helperService.formatDutchDate(pendingTerms.activatedAt)}
              </span>
            )}

            {pendingTerms.termsUrls?.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                {pendingTerms.termsUrls.map((item) => (
                  <Button
                    key={item.url}
                    variant="text"
                    color="secondary"
                    onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                  >
                    {item.displayName || 'Bekijk de voorwaarden'}
                    <Icon sx={{ fontSize: '1.25rem' }}>chevron_right</Icon>
                  </Button>
                ))}
              </Stack>
            )}

          </>
        )}
      </DialogContent>

      {!loading && !loadError && pendingTerms && (
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
              />
            }
            label="Ik ga akkoord"
          />
          <Button
            variant="contained"
            color="secondary"
            disabled={!agreed || acknowledging}
            startIcon={acknowledging ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={handleAcknowledge}
          >
            Ga verder naar infraplannen.nl
          </Button>
        </DialogActions>
      )}
      {acknowledgeError && (
        <ErrorMessage message="Het bevestigen van de voorwaarden is mislukt. Probeer het opnieuw." />
      )}
    </Dialog>
  );
};

export default TermsAcknowledgementDialog;
